/**
 * SessionSnapshot — UX-PLAN Pillar D (Gap 4).
 *
 * The inspector and the thread were two consumers of `chatSubagents +
 * sessions` who agreed only when both caches were warm. Every other
 * moment — the registry poll hadn't landed, the catalog just refetched,
 * the user switched tabs mid-flight — they disagreed. The thread said
 * "3 running children", the inspector said "No projects or subagents
 * yet". That drift is the bug.
 *
 * The fix is structural: compute a single `SessionSnapshot` derived
 * value from the union of every cache the two surfaces previously read
 * separately, and route both surfaces through it. The snapshot is
 * derived once per render cycle (called from
 * `buildFleetChatRenderProps`) so the values cannot disagree by
 * construction — the two surfaces read the same object.
 *
 * The snapshot is intentionally **pure**: callers pass in the inputs
 * (state slice + sessions catalog), the function returns the joined
 * view. No `@state()` mutation, no DOM, no event subscriptions. That
 * keeps the unit test honest and lets the inspector / thread read
 * identical data even if they are mounted in different orders.
 *
 * Invariant (asserted by `assertSessionSnapshotInvariant`):
 *   - snapshot.parent === chatTabMeta[active].parentKey
 *   - snapshot.children.length === subagentsBySession.get(active)?.length
 * These are the two failure modes the old code had — see the Pillar D
 * section of UX-PLAN.md for the concrete bug list. The assert fires
 * `console.error` in development, no-op in production builds, so a
 * drift never silently regresses.
 */

import type { SessionContextBreakdown } from "../controllers/chat-context-breakdown.ts";
import type { PalaceContinuitySnapshot } from "../controllers/chat-palace-continuity.ts";
import type { SessionEnvironmentExplainResult, SessionsListResult } from "../types.ts";
import type { ChatSubagentCard } from "../ui-types.ts";
import type { ChatSessionContextMeta } from "../views/chat-session-context.ts";
import { computeSessionLiveness, type SessionLiveness } from "./session-liveness.ts";
import { filterActiveSubagents } from "./subagent-run-status.ts";
import { restoreChatSubagentsForSession } from "./subagents-by-session.ts";

/**
 * Coarse-grained session status. Coarser than `ChatSubagentCard.status`
 * (which is per-run) and finer than a boolean. Mirrors the Pillar A
 * union; every row + every chip + every "no subagents" empty state
 * should bind to this value, not to the raw card status.
 *
 * Derivation order matters — the first matching rule wins:
 *   - `failed`    — lastError set
 *   - `blocked`   — fleet question pending or compaction in progress
 *   - `offline`   — gateway disconnected
 *   - `running`   — chat.run active or at least one active subagent
 *   - `idle`      — none of the above
 */
export type SessionStatus = "running" | "idle" | "failed" | "blocked" | "offline";

/**
 * Run state, derived via `computeSessionLiveness`. Includes the
 * `liveReason` label (chat-run vs subagent vs fleet-question vs
 * compaction) so chips and the run log can render the same
 * explanation without re-deriving.
 */
export type SessionSnapshotRunState = SessionLiveness;

/**
 * Inputs the snapshot derives from. Plain object — no Lit, no `state`
 * proxy. Both the inspector and the thread pass the same shape so
 * they get the same answer.
 *
 * `subagentCardHost` is the OpenClawApp instance used as a WeakMap
 * key for the per-session subagent stash (see
 * `subagents-by-session.ts`). It's optional so callers without a host
 * (e.g. SSR or isolated view tests) can still derive a snapshot.
 */
export type SessionSnapshotInputs = {
  sessionKey: string;
  sessions: SessionsListResult | null;
  tabMeta?: Record<string, ChatSessionContextMeta>;
  /** Active-session subagent cards. Source of truth for the live view. */
  chatSubagents?: ChatSubagentCard[];
  /** Per-session subagent stash (Pillar B source of truth). */
  subagentCardHost?: object;
  /** Environment probe result for the active session. */
  environment?: SessionEnvironmentExplainResult | null;
  /** Palace continuity snapshot for the active session. */
  continuity?: PalaceContinuitySnapshot | null;
  /** Context-usage breakdown for the active session. */
  contextBreakdown?: SessionContextBreakdown | null;
  /** Last error observed by the UI (global in Pillar D; per-session in Pillar B). */
  lastError?: string | null;
  /** True if a chat.run is currently in flight for the active session. */
  runActive?: boolean;
  /** True when inject / announce / spawn background finals are still settling. */
  backgroundRunsActive?: boolean;
  /** True if the session is currently compacting. */
  compactionActive?: boolean;
  /** True if a fleet question is pending on the active session. */
  fleetQuestionPending?: boolean;
  /** Wall-clock timestamp of the most recent chat event for staleness. */
  lastEventAtMs?: number | null;
  /** Current time for the staleness check. */
  nowMs?: number;
  /** Floci heartbeat TTL. Match `FLEET_HEARTBEAT_TTL_MS` in production. */
  heartbeatTtlMs?: number;
  /** Gateway connection state. */
  connected?: boolean;
};

/**
 * The derived snapshot. Consumers should treat this as immutable —
 * recompute via `buildSessionSnapshot` on each render cycle, never
 * mutate the object in place.
 */
export type SessionSnapshot = {
  sessionKey: string;
  /** Parent session key (authoritative: `chatTabMeta[active].parentKey`). */
  parent: string | null;
  /**
   * Children of the active session. Sourced from the per-session
   * stash (`subagents-by-session`) so it survives a cache wipe;
   * falls back to `chatSubagents` when the host has no stash yet
   * (cold start before `activateChatSession` has run).
   */
  children: ChatSubagentCard[];
  /** Coarse session status — drives the row chip + composer disabledReason. */
  status: SessionStatus;
  /** Environment probe for the active session (may be null while loading). */
  environment: SessionEnvironmentExplainResult | null;
  /** Palace continuity snapshot for the active session. */
  continuity: PalaceContinuitySnapshot | null;
  /** Context-usage breakdown for the active session. */
  contextBreakdown: SessionContextBreakdown | null;
  /** Last error observed for this session (or global fallback). */
  lastError: string | null;
  /** Run state derived via `computeSessionLiveness`. */
  runState: SessionSnapshotRunState;
  /**
   * True iff the catalog (`sessions`) has at least one row whose
   * `spawnedBy` matches the active session. Useful for distinguishing
   * "no subagents ever" from "registry poll hasn't landed yet" without
   * exposing the raw catalog.
   */
  catalogHasSpawnedChildren: boolean;
};

const DEFAULT_HEARTBEAT_TTL_MS = 30_000;

/**
 * Compute the snapshot for a single render cycle. Pure function —
 * same inputs always produce the same output.
 *
 * The children derivation prefers the per-session stash so a
 * switch-and-back retains the cards (per Pillar B's "no teardown"
 * goal). The fallback to `chatSubagents` keeps the legacy
 * "active-session cards only" path working in cold-start situations
 * where the stash is empty because the host has never seen the
 * session before.
 */
export function buildSessionSnapshot(inputs: SessionSnapshotInputs): SessionSnapshot {
  const sessionKey = (inputs.sessionKey ?? "").trim();
  const parent = (inputs.tabMeta?.[sessionKey]?.parentKey ?? "").trim() || null;

  const children = resolveChildren(inputs, sessionKey);

  const lastError = inputs.lastError ?? null;
  const runActive = inputs.runActive ?? false;
  const backgroundRunsActive = inputs.backgroundRunsActive ?? false;
  const compactionActive = inputs.compactionActive ?? false;
  const fleetQuestionPending = inputs.fleetQuestionPending ?? false;
  const connected = inputs.connected ?? true;
  // One clock for the whole snapshot: liveness, status and the subagent
  // staleness budget must not disagree about "now" within a single render.
  const nowMs = inputs.nowMs ?? Date.now();

  const runState = computeSessionLiveness({
    runActive,
    backgroundRunsActive,
    compactionActive,
    fleetQuestion: fleetQuestionPending ? { sessionKey } : null,
    subagents: children,
    lastEventAtMs: inputs.lastEventAtMs ?? null,
    nowMs,
    heartbeatTtlMs: inputs.heartbeatTtlMs ?? DEFAULT_HEARTBEAT_TTL_MS,
  });

  const status = resolveStatus({
    lastError,
    runActive,
    compactionActive,
    fleetQuestionPending,
    connected,
    children,
    nowMs,
  });

  const catalogHasSpawnedChildren = hasCatalogSpawnedChildren(inputs, sessionKey);

  return {
    sessionKey,
    parent,
    children,
    status,
    environment: inputs.environment ?? null,
    continuity: inputs.continuity ?? null,
    contextBreakdown: inputs.contextBreakdown ?? null,
    lastError,
    runState,
    catalogHasSpawnedChildren,
  };
}

function resolveChildren(inputs: SessionSnapshotInputs, sessionKey: string): ChatSubagentCard[] {
  if (!sessionKey) {
    return inputs.chatSubagents ?? [];
  }
  // Prefer the per-host stash so a session switch-and-back retains
  // the children that were visible before the user moved away.
  // `restoreChatSubagentsForSession` already merges catalog + stash
  // for us — we just call it with whatever sessions catalog the caller
  // already has in hand.
  if (inputs.subagentCardHost) {
    return restoreChatSubagentsForSession(
      inputs.subagentCardHost,
      sessionKey,
      inputs.sessions ?? null,
    );
  }
  return inputs.chatSubagents ?? [];
}

function resolveStatus(params: {
  lastError: string | null;
  runActive: boolean;
  compactionActive: boolean;
  fleetQuestionPending: boolean;
  connected: boolean;
  children: ChatSubagentCard[];
  nowMs: number;
}): SessionStatus {
  if (params.lastError) {
    return "failed";
  }
  if (params.fleetQuestionPending || params.compactionActive) {
    return "blocked";
  }
  if (!params.connected) {
    return "offline";
  }
  if (params.runActive) {
    return "running";
  }
  // A child that is still flagged active but has gone quiet past the staleness
  // budget must not keep the whole session pinned to "running" — that is what
  // left the composer advertising a live subagent after the plan concluded.
  if (filterActiveSubagents(params.children, params.nowMs).length > 0) {
    return "running";
  }
  return "idle";
}

function hasCatalogSpawnedChildren(inputs: SessionSnapshotInputs, sessionKey: string): boolean {
  if (!sessionKey) {
    return false;
  }
  const rows = inputs.sessions?.sessions ?? [];
  for (const row of rows) {
    if (row.spawnedBy?.trim() === sessionKey) {
      return true;
    }
  }
  return false;
}

/**
 * Pillar D invariant guard. Logs a `console.error` in development so
 * drift between the snapshot and the underlying state is visible
 * without breaking the UI. No-op in production (`import.meta.env.PROD`).
 *
 * The check fires only when the inputs are non-empty — cold-start
 * callers legitimately have an empty stash and an empty tabMeta, and
 * asserting against those would produce noise. The point of the
 * invariant is to catch the failure mode where the snapshot and the
 * authoritative source disagree while both have data.
 */
export function assertSessionSnapshotInvariant(params: {
  snapshot: SessionSnapshot;
  tabMeta: Record<string, ChatSessionContextMeta> | undefined;
  /**
   * Length of the per-session subagent stash for the active session.
   * Pass `undefined` when no stash is available (cold start).
   */
  stashedChildCount: number | undefined;
  /** Set to false to silence the assert (e.g. inside the test). */
  enabled?: boolean;
}): void {
  if (params.enabled === false) {
    return;
  }
  // Enforce in the test runner AND in development builds. The original
  // implementation gated on `!import.meta.env.PROD`, but Vitest's
  // default config sets `PROD=true` even in `NODE_ENV=test`, so the
  // drift assertion silently no-op'd from CI and the regression never
  // surfaced. We now gate on `MODE === "production"` so the assert
  // fires whenever the bundle is NOT a shipped production build —
  // that includes the test runner, `vite dev`, and the staging
  // integration harness. The production build's MODE is statically
  // replaced with `"production"` by Vite, so the branch tree-shakes
  // away in shipped code.
  if (import.meta.env.MODE === "production") {
    return;
  }
  const { snapshot } = params;
  if (!snapshot.sessionKey) {
    return;
  }
  const metaParent = params.tabMeta?.[snapshot.sessionKey]?.parentKey?.trim() || null;
  if (metaParent !== snapshot.parent) {
    // eslint-disable-next-line no-console
    console.error("[session-snapshot] drift: snapshot.parent !== chatTabMeta[active].parentKey", {
      sessionKey: snapshot.sessionKey,
      snapshotParent: snapshot.parent,
      metaParent,
    });
  }
  if (
    params.stashedChildCount !== undefined &&
    params.stashedChildCount !== snapshot.children.length
  ) {
    // eslint-disable-next-line no-console
    console.error(
      "[session-snapshot] drift: snapshot.children.length !== subagentsBySession.get(active).length",
      {
        sessionKey: snapshot.sessionKey,
        snapshotChildren: snapshot.children.length,
        stashedChildCount: params.stashedChildCount,
      },
    );
  }
}
